import { useState, type ReactNode } from 'react';

import { DEFAULT_PAGINATION_PAGE_SIZE } from '@/consts/table';
import type { SelectionMode } from '@/features/table/components/DataGridTree';
import { GridFooter } from '@/features/table/components/GridFooter';
import { GridToolbar } from '@/features/table/components/GridToolbar';
import LoadingOverlay from '@/features/table/components/LoadingOverlay';
import { NoRowsOverlay } from '@/features/table/components/NoRowsOverlay';
import { denseTheme } from '@/features/table/consts/config';
import { useNoRowsOverlay } from '@/features/table/hooks/useNoRowsOverlay';
import { useGridLayoutIntegration } from '@/modules/PMS/features/grid-layout';
import { cn } from '@/utils/cn';
import type { ColDef } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';

import styles from '@/features/table/styles/DataGrid.module.css';

interface DataGridProps<T = unknown> {
  columnDefs: AgGridReactProps['columnDefs'];
  rowData: AgGridReactProps['rowData'];
  onGridReady?: AgGridReactProps['onGridReady'];
  domLayout?: AgGridReactProps['domLayout'];
  pagination?: AgGridReactProps['pagination'];
  paginationPageSize?: AgGridReactProps['paginationPageSize'];
  loading?: AgGridReactProps['loading'];
  height?: number | string;
  width?: number | string;
  defaultColDef?: AgGridReactProps['defaultColDef'];
  groupDisplayType?: AgGridReactProps['groupDisplayType'];
  rowGroupPanelShow?: AgGridReactProps['rowGroupPanelShow'];
  autoGroupColumnDef?: AgGridReactProps['autoGroupColumnDef'];
  groupDefaultExpanded?: AgGridReactProps['groupDefaultExpanded'];
  initialGroupOrderComparator?: AgGridReactProps['initialGroupOrderComparator'];
  showFooter?: boolean;
  context?: AgGridReactProps<T>['context'];
  showCheckboxSelection?: boolean;
  selectionMode?: SelectionMode;
  selectionColumnDef?: AgGridReactProps<T>['selectionColumnDef'];
  getRowId?: AgGridReactProps<T>['getRowId'];
  onSelectionChanged?: AgGridReactProps<T>['onSelectionChanged'];
  onCellValueChanged?: AgGridReactProps<T>['onCellValueChanged'];
  className?: string;
  toolbarActions?: ReactNode;
  filterButton?: ReactNode;
  showSearchBar?: boolean;
  showColumnPicker?: boolean;
  showToolbarLabels?: boolean;
  showSaveLayout?: boolean;
  tableId?: string;
}

export const DataGrid = <T extends Record<string, unknown>>(props: DataGridProps<T>) => {
  const {
    columnDefs,
    rowData,
    onGridReady,
    domLayout = 'autoHeight',
    pagination = false,
    paginationPageSize = DEFAULT_PAGINATION_PAGE_SIZE,
    loading = false,
    height = '100%',
    width = '100%',
    defaultColDef,
    groupDisplayType,
    rowGroupPanelShow,
    autoGroupColumnDef,
    groupDefaultExpanded,
    initialGroupOrderComparator,
    showFooter = true,
    showCheckboxSelection = false,
    selectionMode = 'multiRow',
    selectionColumnDef,
    getRowId,
    onSelectionChanged,
    className = '',
    toolbarActions,
    filterButton,
    showSearchBar = true,
    showColumnPicker = true,
    showToolbarLabels = true,
    showSaveLayout = true,
    tableId
  } = props;

  const totalRecords = Array.isArray(rowData) ? rowData.length : 0;
  const [quickFilterText, setQuickFilterText] = useState('');

  const {
    canSaveLayout,
    isLayoutReady,
    saveLayout,
    isSaving,
    visibleColumnDefs,
    allColumns,
    visibleColumnIds,
    handleVisibilityChange,
    createGridReadyHandler
  } = useGridLayoutIntegration<T>({
    tableId,
    showSaveLayout,
    columnDefs: columnDefs as ColDef<T>[]
  });

  const { handleGridReady, handleModelUpdated, displayedRowCount } = useNoRowsOverlay<T>({
    onGridReady: createGridReadyHandler(onGridReady),
    totalRowCount: totalRecords
  });

  const handleQuickFilterChange = (value: string) => {
    setQuickFilterText(value);
  };

  const showToolbar = showSearchBar || showColumnPicker || canSaveLayout || toolbarActions !== undefined;

  // Wait for layout query to complete before rendering (only if there might be a saved layout)
  if (canSaveLayout && !isLayoutReady) {
    return (
      <div className={cn(styles.dataGridContainer ?? '', className)} style={{ height, width }}>
        <LoadingOverlay />
      </div>
    );
  }

  return (
    <div className={cn(styles.dataGridContainer ?? '', className)} style={{ height, width }}>
      {showToolbar && (
        <GridToolbar
          columns={allColumns}
          visibleColumns={visibleColumnIds}
          onVisibilityChange={handleVisibilityChange}
          onQuickFilterChange={handleQuickFilterChange}
          toolbarActions={toolbarActions}
          filterButton={filterButton}
          showSearchBar={showSearchBar}
          showColumnPicker={showColumnPicker}
          showToolbarLabels={showToolbarLabels}
          showSaveLayout={canSaveLayout}
          onSaveLayout={canSaveLayout ? saveLayout : undefined}
          isSavingLayout={isSaving}
        />
      )}
      <AgGridReact
        theme={denseTheme}
        columnDefs={visibleColumnDefs}
        rowData={rowData}
        defaultColDef={defaultColDef}
        onGridReady={handleGridReady}
        onModelUpdated={handleModelUpdated}
        domLayout={domLayout}
        pagination={pagination}
        paginationPageSize={paginationPageSize}
        loading={loading}
        loadingOverlayComponent={LoadingOverlay}
        noRowsOverlayComponent={NoRowsOverlay}
        quickFilterText={quickFilterText}
        groupDisplayType={groupDisplayType}
        rowGroupPanelShow={rowGroupPanelShow}
        autoGroupColumnDef={autoGroupColumnDef}
        groupDefaultExpanded={groupDefaultExpanded}
        initialGroupOrderComparator={initialGroupOrderComparator}
        tooltipShowMode="whenTruncated"
        singleClickEdit={true}
        stopEditingWhenCellsLoseFocus={true}
        animateRows={true}
        rowSelection={
          showCheckboxSelection
            ? {
                mode: selectionMode,
                checkboxes: true,
                enableClickSelection: false,
                ...(selectionMode === 'multiRow' && { headerCheckbox: true })
              }
            : undefined
        }
        {...(props.context !== undefined && { context: props.context })}
        {...(getRowId !== undefined && { getRowId })}
        {...(onSelectionChanged !== undefined && { onSelectionChanged })}
        {...(props.onCellValueChanged !== undefined && { onCellValueChanged: props.onCellValueChanged })}
        selectionColumnDef={showCheckboxSelection ? selectionColumnDef : undefined}
      />
      {showFooter && <GridFooter totalRecords={displayedRowCount} />}
    </div>
  );
};
